import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";

const field = "min-h-[42px] border border-[#C69A3B]/60 bg-white px-2 text-sm";

export default function AdminMedia({ refreshToken, onChange }) {
  const { t, localized } = useI18n();
  const [events, setEvents] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [filters, setFilters] = useState({ event_id: "", album_id: "", kind: "", published: "", watermark: "", download_enabled: "", q: "" });
  const [data, setData] = useState({ media: [], total: 0 });
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filters.event_id) return setAlbums([]);
    api.get(`/admin/events/${filters.event_id}/albums`).then((r) => setAlbums(r.data)).catch(() => {});
  }, [filters.event_id]);

  const load = useCallback(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "") return;
      params[k] = v === "true" ? true : v === "false" ? false : v;
    });
    api.get("/admin/media", { params: { ...params, page_size: 60 } }).then((r) => setData(r.data)).catch(() => {});
  }, [filters]);

  useEffect(load, [load, refreshToken]);

  const bulk = async (payload) => {
    await api.post("/admin/media/bulk", { media_ids: selected, ...payload });
    setSelected([]);
    load();
    onChange?.();
    toast.success(t("btn.save"));
  };

  const sel = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div data-testid="admin-media-panel">
      <div className="flex flex-wrap gap-3">
        <select className={field} data-testid="media-filter-event" value={filters.event_id} onChange={(e) => setFilters((f) => ({ ...f, event_id: e.target.value, album_id: "" }))}>
          <option value="">{t("common.all")} — Eventos</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{localized(ev, "name")}</option>
          ))}
        </select>
        <select className={field} data-testid="media-filter-album" value={filters.album_id} onChange={(e) => setFilters((f) => ({ ...f, album_id: e.target.value }))}>
          <option value="">{t("common.all")} — Álbumes</option>
          {albums.map((a) => (
            <option key={a.id} value={a.id}>{localized(a, "name")}</option>
          ))}
        </select>
        <select className={field} data-testid="media-filter-kind" value={filters.kind} onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}>
          <option value="">Foto / Video</option>
          <option value="photo">Foto</option>
          <option value="video">Video</option>
        </select>
        <select className={field} data-testid="media-filter-published" value={filters.published} onChange={(e) => setFilters((f) => ({ ...f, published: e.target.value }))}>
          <option value="">{t("common.published")} / {t("common.draft")}</option>
          <option value="true">{t("common.published")}</option>
          <option value="false">{t("common.draft")}</option>
        </select>
        <select className={field} data-testid="media-filter-watermark" value={filters.watermark} onChange={(e) => setFilters((f) => ({ ...f, watermark: e.target.value }))}>
          <option value="">{t("common.watermark")}</option>
          <option value="true">{t("common.yes")}</option>
          <option value="false">{t("common.no")}</option>
        </select>
        <input className={field} placeholder="Buscar archivo, leyenda, etiqueta" data-testid="media-search-input" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3 text-sm">
        <span className="label-mono">{data.total} medios</span>
        <button type="button" data-testid="media-select-all" onClick={() => setSelected(data.media.map((m) => m.id))} className="underline">
          Seleccionar todo
        </button>
        {selected.length > 0 && (
          <>
            <span className="label-mono">{selected.length} sel.</span>
            <GoldButton variant="outline" data-testid="media-bulk-publish" onClick={() => bulk({ published: true })}>{t("common.published")}</GoldButton>
            <GoldButton variant="outline" data-testid="media-bulk-unpublish" onClick={() => bulk({ published: false })}>{t("common.draft")}</GoldButton>
            <GoldButton variant="outline" data-testid="media-bulk-watermark-on" onClick={() => bulk({ watermark: true })}>{t("common.watermark")} ✓</GoldButton>
            <GoldButton variant="outline" data-testid="media-bulk-watermark-off" onClick={() => bulk({ watermark: false })}>{t("common.watermark")} ✕</GoldButton>
            <GoldButton variant="outline" data-testid="media-bulk-download-on" onClick={() => bulk({ download_enabled: true })}>{t("common.downloads")} ✓</GoldButton>
            <GoldButton variant="outline" data-testid="media-bulk-download-off" onClick={() => bulk({ download_enabled: false })}>{t("common.downloads")} ✕</GoldButton>
            {albums.length > 0 && (
              <select className={field} data-testid="media-bulk-move" onChange={(e) => e.target.value && bulk({ album_id: e.target.value })} defaultValue="">
                <option value="">Mover a álbum…</option>
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>{localized(a, "name")}</option>
                ))}
              </select>
            )}
            <GoldButton data-testid="media-bulk-delete" onClick={() => bulk({ delete: true })}>Eliminar</GoldButton>
          </>
        )}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="admin-media-grid">
        {data.media.map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`admin-media-tile-${m.id}`}
            onClick={() => sel(m.id)}
            className={`overflow-hidden border text-left ${selected.includes(m.id) ? "border-[#8F3329]" : "border-[#C69A3B]/30"}`}
          >
            <div className="aspect-square overflow-hidden bg-[#D8D3CB]">
              <img
                src={m.external_url || mediaUrl(m, "thumb")}
                alt={m.filename}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="p-2">
              <p className="truncate text-xs">{m.filename}</p>
              <p className="label-mono">{m.published ? t("common.published") : t("common.draft")}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
